import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import {
  BookOpen,
  ArrowLeft,
  MessageCircle,
  PenLine,
  Send,
  Trash2,
  Users,
  Calendar,
  Trophy,
  Loader2,
  ChevronDown,
  ChevronUp,
  Lock,
  CheckSquare,
  Square,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

// Import API URL from utility
import { API_URL } from '../lib/api';

const ReadathonDetail = () => {
  const { readathonId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [readathon, setReadathon] = useState(null);
  const [myProgress, setMyProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reading');
  
  // Journal state
  const [journals, setJournals] = useState([]);
  const [newJournal, setNewJournal] = useState({
    book_name: '',
    chapter: 1,
    favourite_part: '',
    what_learned: '',
    how_apply: '',
    journal_text: '',
    is_public: false
  });
  const [customQuestions, setCustomQuestions] = useState([]);
  const [customAnswers, setCustomAnswers] = useState({});
  const [expandedJournal, setExpandedJournal] = useState(null);
  const [publicJournals, setPublicJournals] = useState([]);
  
  // Discussion state
  const [discussions, setDiscussions] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Reading checklist state
  const [readingChecklist, setReadingChecklist] = useState({});
  const [bibleBooks, setBibleBooks] = useState([]);
  const [expandedBook, setExpandedBook] = useState(null);
  const [savingReading, setSavingReading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchReadathon();
    if (isAuthenticated) {
      fetchMyProgress();
      fetchJournals();
    }
    fetchDiscussions();
    fetchCustomQuestions();
    fetchPublicJournals();
    fetchBibleBooks();
    if (isAuthenticated) {
      fetchReadingChecklist();
    }
  }, [readathonId, isAuthenticated]);

  const fetchReadathon = async () => {
    try {
      const response = await fetch(`${API_URL}/api/readathons/${readathonId}`);
      if (response.ok) {
        const data = await response.json();
        setReadathon(data);
        if (data.books?.length > 0) {
          setNewJournal(prev => ({ ...prev, book_name: data.books[0] }));
        }
      }
    } catch (err) {
      console.error('Error fetching readathon:', err);
    }
    setLoading(false);
  };

  const fetchMyProgress = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/readathons/${readathonId}/my-progress`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setMyProgress(data);
      }
    } catch (err) {
      console.error('Error fetching progress:', err);
    }
  };

  const fetchJournals = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/readathons/${readathonId}/my-journals`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setJournals(data);
      }
    } catch (err) {
      console.error('Error fetching journals:', err);
    }
  };

  const fetchPublicJournals = async () => {
    try {
      const response = await fetch(`${API_URL}/api/readathons/${readathonId}/public-journals`);
      if (response.ok) {
        setPublicJournals(await response.json());
      }
    } catch (err) {
      console.error('Error fetching public journals:', err);
    }
  };

  const fetchBibleBooks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bible/books`);
      if (response.ok) {
        setBibleBooks(await response.json());
      }
    } catch (err) {
      console.error('Error fetching bible books:', err);
    }
  };

  const fetchReadingChecklist = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/readathons/${readathonId}/my-reading`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setReadingChecklist(data.completed_chapters || {});
      }
    } catch (err) {
      console.error('Error fetching reading checklist:', err);
    }
  };

  const saveReadingChecklist = async (newChecklist) => {
    setSavingReading(true);
    try {
      await fetch(`${API_URL}/api/readathons/${readathonId}/my-reading`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ completed_chapters: newChecklist })
      });
      fetchMyProgress();
    } catch (err) {
      console.error('Error saving reading progress:', err);
    }
    setSavingReading(false);
  };

  const toggleChapter = (bookName, chapter) => {
    const current = readingChecklist[bookName] || [];
    let updated;
    if (current.includes(chapter)) {
      updated = current.filter(c => c !== chapter);
    } else {
      updated = [...current, chapter].sort((a, b) => a - b);
    }
    const newChecklist = { ...readingChecklist, [bookName]: updated };
    if (updated.length === 0) delete newChecklist[bookName];
    setReadingChecklist(newChecklist);
    saveReadingChecklist(newChecklist);
  };

  const toggleAllChapters = (bookName, totalChapters) => {
    const current = readingChecklist[bookName] || [];
    let newChecklist;
    if (current.length === totalChapters) {
      newChecklist = { ...readingChecklist };
      delete newChecklist[bookName];
    } else {
      const allChapters = Array.from({ length: totalChapters }, (_, i) => i + 1);
      newChecklist = { ...readingChecklist, [bookName]: allChapters };
    }
    setReadingChecklist(newChecklist);
    saveReadingChecklist(newChecklist);
  };

  const getReadingPlanBooks = () => {
    if (!readathon) return [];
    if (readathon.reading_plan?.length > 0) {
      return readathon.reading_plan.map(p => {
        const bibleBook = bibleBooks.find(b => b.name === p.book);
        return { ...p, totalChapters: bibleBook?.chapters || 0, verses: bibleBook?.verses || [] };
      });
    }
    return (readathon.books || []).map(name => {
      const bibleBook = bibleBooks.find(b => b.name === name);
      return { book: name, chapters: bibleBook ? Array.from({ length: bibleBook.chapters }, (_, i) => i + 1) : [], totalChapters: bibleBook?.chapters || 0, verses: bibleBook?.verses || [] };
    });
  };

  const fetchDiscussions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/readathons/${readathonId}/discussions`);
      if (response.ok) {
        const data = await response.json();
        setDiscussions(data);
      }
    } catch (err) {
      console.error('Error fetching discussions:', err);
    }
  };

  const fetchCustomQuestions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/readathons/${readathonId}/questions`);
      if (response.ok) {
        const data = await response.json();
        setCustomQuestions(data);
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
    }
  };

  const handleSignup = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/readathons/${readathonId}/signup`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      if (response.ok) {
        toast.success("You're signed up!");
        fetchMyProgress();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to sign up');
      }
    } catch (err) {
      toast.error('Failed to sign up');
    }
  };

  const submitJournal = async () => {
    if (!newJournal.book_name) {
      toast.error('Please select a book');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/readathons/${readathonId}/journal`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            ...newJournal,
            custom_answers: customAnswers
          })
        }
      );

      if (response.ok) {
        toast.success('Journal entry saved!');
        setNewJournal({
          book_name: readathon?.books?.[0] || '',
          chapter: newJournal.chapter + 1,
          favourite_part: '',
          what_learned: '',
          how_apply: '',
          journal_text: '',
          is_public: false
        });
        setCustomAnswers({});
        fetchJournals();
        fetchPublicJournals();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save journal');
      }
    } catch (err) {
      toast.error('Failed to save journal');
    }
    setSubmitting(false);
  };

  const deleteJournal = async (journalId) => {
    if (!window.confirm('Delete this journal entry?')) return;

    try {
      const response = await fetch(
        `${API_URL}/api/readathons/${readathonId}/journal/${journalId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      if (response.ok) {
        toast.success('Journal deleted');
        fetchJournals();
      }
    } catch (err) {
      toast.error('Failed to delete journal');
    }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/readathons/${readathonId}/discussions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify({ content: newComment })
        }
      );

      if (response.ok) {
        toast.success('Comment posted!');
        setNewComment('');
        fetchDiscussions();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to post comment');
      }
    } catch (err) {
      toast.error('Failed to post comment');
    }
    setSubmitting(false);
  };

  const deleteComment = async (commentId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/readathons/${readathonId}/discussions/${commentId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      if (response.ok) {
        toast.success('Comment deleted');
        fetchDiscussions();
      }
    } catch (err) {
      toast.error('Failed to delete comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-sky-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!readathon) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-sky-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">Readathon not found</p>
          <Button onClick={() => navigate('/readathon')} className="mt-4">
            Back to Readathons
          </Button>
        </div>
      </div>
    );
  }

  const isSignedUp = myProgress?.signed_up;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-sky-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-emerald-100 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/readathon')}
            className="text-slate-600 hover:text-emerald-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Readathons
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Readathon Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white mb-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Trophy className="w-5 h-5" />
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  readathon.is_active ? 'bg-white/20' : 'bg-slate-600/50'
                }`}>
                  {readathon.is_active ? 'Active' : 'Completed'}
                </span>
              </div>
              <h1 className="text-3xl font-serif font-bold mb-2">{readathon.title}</h1>
              <p className="text-emerald-100 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(readathon.start_date).toLocaleDateString()} - {new Date(readathon.end_date).toLocaleDateString()}
              </p>
            </div>
            
            {!isSignedUp && readathon.is_active && (
              <Button
                onClick={handleSignup}
                className="bg-white text-emerald-600 hover:bg-emerald-50"
              >
                Join Now
              </Button>
            )}
          </div>
          
          <p className="mt-4 text-emerald-50">{readathon.description}</p>
          
          <div className="flex flex-wrap gap-2 mt-4">
            {readathon.books?.map((book, idx) => (
              <span key={idx} className="bg-white/20 px-3 py-1 rounded-full text-sm">
                {book}
              </span>
            ))}
          </div>

          {isSignedUp && (
            <div className="mt-6 bg-white/10 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Your Progress</span>
                <span className="text-lg font-bold">{myProgress.progress_percent || 0}%</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white transition-all"
                  style={{ width: `${myProgress.progress_percent || 0}%` }}
                />
              </div>
              <p className="text-xs text-emerald-100 mt-2">
                {myProgress.chapters_read || 0} of {readathon.total_chapters} chapters
              </p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('reading')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'reading'
                ? 'text-emerald-600 border-b-2 border-emerald-500'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <CheckSquare className="w-4 h-4 inline mr-2" />
            Reading Plan
          </button>
          <button
            onClick={() => setActiveTab('journal')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'journal'
                ? 'text-emerald-600 border-b-2 border-emerald-500'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <PenLine className="w-4 h-4 inline mr-2" />
            My Journal
          </button>
          <button
            onClick={() => setActiveTab('community')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'community'
                ? 'text-emerald-600 border-b-2 border-emerald-500'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Community ({publicJournals.length})
          </button>
          <button
            onClick={() => setActiveTab('discussion')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'discussion'
                ? 'text-emerald-600 border-b-2 border-emerald-500'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <MessageCircle className="w-4 h-4 inline mr-2" />
            Discussion ({discussions.length})
          </button>
        </div>

        {/* Reading Plan Tab */}
        {activeTab === 'reading' && (
          <div className="space-y-4">
            {!isSignedUp ? (
              <div className="bg-white rounded-2xl border p-8 text-center">
                <Lock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-800 mb-2">Join to Track Reading</h3>
                <p className="text-slate-600 mb-4">Sign up to track which chapters you've read.</p>
                {readathon.is_active && (
                  <Button onClick={handleSignup} className="bg-emerald-500 hover:bg-emerald-600 text-white">Join Readathon</Button>
                )}
              </div>
            ) : (
              <>
                {savingReading && <p className="text-xs text-emerald-600 animate-pulse">Saving progress...</p>}
                {getReadingPlanBooks().map((planBook) => {
                  const completedForBook = readingChecklist[planBook.book] || [];
                  const allDone = completedForBook.length === planBook.totalChapters && planBook.totalChapters > 0;
                  
                  return (
                    <div key={planBook.book} className="bg-white rounded-xl border overflow-hidden">
                      <div 
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedBook(expandedBook === planBook.book ? null : planBook.book)}
                      >
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleAllChapters(planBook.book, planBook.totalChapters); }}
                            className={`w-6 h-6 rounded flex items-center justify-center border-2 transition-colors ${
                              allDone ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-emerald-400'
                            }`}
                          >
                            {allDone && <Check className="w-4 h-4" />}
                          </button>
                          <div>
                            <p className={`font-bold ${allDone ? 'text-emerald-600' : 'text-slate-800'}`}>{planBook.book}</p>
                            <p className="text-xs text-slate-500">{completedForBook.length} of {planBook.totalChapters} chapters read</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500 rounded-full transition-all"
                              style={{ width: `${planBook.totalChapters > 0 ? (completedForBook.length / planBook.totalChapters * 100) : 0}%` }}
                            />
                          </div>
                          {expandedBook === planBook.book ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                        </div>
                      </div>
                      
                      {expandedBook === planBook.book && (
                        <div className="px-4 pb-4 border-t bg-slate-50">
                          <p className="text-xs text-slate-500 py-2">Tick each chapter as you read it:</p>
                          <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2">
                            {(planBook.chapters || []).map((ch) => {
                              const isRead = completedForBook.includes(ch);
                              return (
                                <button
                                  key={ch}
                                  onClick={() => toggleChapter(planBook.book, ch)}
                                  className={`p-2 rounded-lg text-sm font-medium transition-all ${
                                    isRead 
                                      ? 'bg-emerald-500 text-white shadow-sm' 
                                      : 'bg-white border border-slate-200 text-slate-600 hover:border-emerald-300 hover:bg-emerald-50'
                                  }`}
                                >
                                  {isRead && <Check className="w-3 h-3 inline mr-0.5" />}
                                  Ch {ch}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* Journal Tab */}
        {activeTab === 'journal' && (
          <div className="space-y-6">
            {!isSignedUp && journals.length === 0 ? (
              <div className="bg-white rounded-2xl border p-8 text-center">
                <Lock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-slate-800 mb-2">Join to Start Journaling</h3>
                <p className="text-slate-600 mb-4">Sign up for this readathon to track your progress and write journal entries.</p>
                {readathon.is_active && (
                  <Button onClick={handleSignup} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    Join Readathon
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* New Journal Entry - only show when signed up */}
                {isSignedUp && (
                <div className="bg-white rounded-2xl border p-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <PenLine className="w-5 h-5 text-emerald-500" />
                    New Journal Entry
                  </h3>
                  
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Book</label>
                      <select
                        value={newJournal.book_name}
                        onChange={(e) => setNewJournal({ ...newJournal, book_name: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        {readathon.books?.map((book, idx) => (
                          <option key={idx} value={book}>{book}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Chapter</label>
                      <Input
                        type="number"
                        min="1"
                        value={newJournal.chapter}
                        onChange={(e) => setNewJournal({ ...newJournal, chapter: parseInt(e.target.value) || 1 })}
                      />
                    </div>
                  </div>

                  {/* Standard Questions */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        What was your favourite part? ⭐
                      </label>
                      <Textarea
                        value={newJournal.favourite_part}
                        onChange={(e) => setNewJournal({ ...newJournal, favourite_part: e.target.value })}
                        placeholder="Tell us what you loved most..."
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        What did you learn? 📚
                      </label>
                      <Textarea
                        value={newJournal.what_learned}
                        onChange={(e) => setNewJournal({ ...newJournal, what_learned: e.target.value })}
                        placeholder="Share something new you discovered..."
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        How will you apply this? 🌟
                      </label>
                      <Textarea
                        value={newJournal.how_apply}
                        onChange={(e) => setNewJournal({ ...newJournal, how_apply: e.target.value })}
                        placeholder="How will you use what you learned?"
                        rows={2}
                      />
                    </div>

                    {/* Custom Questions */}
                    {customQuestions.map((q) => (
                      <div key={q.question_id}>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          {q.question_text}
                        </label>
                        <Textarea
                          value={customAnswers[q.question_id] || ''}
                          onChange={(e) => setCustomAnswers({ ...customAnswers, [q.question_id]: e.target.value })}
                          rows={2}
                        />
                      </div>
                    ))}
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Free Journal (Optional) 📝
                      </label>
                      <Textarea
                        value={newJournal.journal_text}
                        onChange={(e) => setNewJournal({ ...newJournal, journal_text: e.target.value })}
                        placeholder="Write anything else you want to remember..."
                        rows={4}
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={submitJournal}
                    disabled={submitting}
                    className="mt-4 bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PenLine className="w-4 h-4 mr-2" />}
                    Save Journal Entry
                  </Button>
                  
                  <div className="mt-3 flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="is-public" 
                      checked={newJournal.is_public} 
                      onChange={(e) => setNewJournal({ ...newJournal, is_public: e.target.checked })}
                      className="rounded"
                    />
                    <label htmlFor="is-public" className="text-sm text-slate-600">
                      Share this entry with the community (visible to other readers)
                    </label>
                  </div>
                </div>
                )}

                {/* Past Journal Entries */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-slate-800">My Journal Entries ({journals.length})</h3>
                  
                  {journals.length === 0 ? (
                    <div className="bg-white rounded-xl border p-6 text-center">
                      <PenLine className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">No journal entries yet. Start writing above!</p>
                    </div>
                  ) : (
                    journals.map((journal) => (
                      <div key={journal.journal_id} className="bg-white rounded-xl border overflow-hidden">
                        <div 
                          className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                          onClick={() => setExpandedJournal(expandedJournal === journal.journal_id ? null : journal.journal_id)}
                        >
                          <div>
                            <p className="font-medium text-slate-800">{journal.book_name} - Chapter {journal.chapter}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(journal.created_at).toLocaleDateString()}
                              {journal.is_public && <span className="ml-2 text-emerald-600">Shared publicly</span>}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:bg-red-50"
                              onClick={(e) => { e.stopPropagation(); deleteJournal(journal.journal_id); }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            {expandedJournal === journal.journal_id ? (
                              <ChevronUp className="w-5 h-5 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                        </div>
                        
                        {expandedJournal === journal.journal_id && (
                          <div className="px-4 pb-4 border-t bg-amber-50/50 space-y-3">
                            <div className="pt-3">
                              <p className="text-xs font-medium text-emerald-600">Favourite Part</p>
                              <p className="text-sm text-slate-700">{journal.favourite_part || <span className="italic text-slate-400">Not answered</span>}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-emerald-600">What I Learned</p>
                              <p className="text-sm text-slate-700">{journal.what_learned || <span className="italic text-slate-400">Not answered</span>}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-emerald-600">How I'll Apply This</p>
                              <p className="text-sm text-slate-700">{journal.how_apply || <span className="italic text-slate-400">Not answered</span>}</p>
                            </div>
                            {/* Custom Question Answers */}
                            {journal.custom_answers && Object.keys(journal.custom_answers).length > 0 && (
                              <div className="pt-2 border-t border-amber-200 space-y-2">
                                {Object.entries(journal.custom_answers).map(([qId, answer]) => {
                                  const question = customQuestions.find(q => q.question_id === qId);
                                  return (
                                    <div key={qId}>
                                      <p className="text-xs font-medium text-purple-600">{question?.question_text || qId}</p>
                                      <p className="text-sm text-slate-700">{answer || <span className="italic text-slate-400">Not answered</span>}</p>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div className="pt-2 border-t border-amber-200">
                              <p className="text-xs font-medium text-emerald-600">Journal Entry</p>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{journal.journal_text || <span className="italic text-slate-400">No journal text</span>}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Community Journals Tab */}
        {activeTab === 'community' && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-800">Community Journal Entries</h3>
            {publicJournals.length === 0 ? (
              <div className="bg-white rounded-xl border p-6 text-center">
                <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No public journal entries yet.</p>
                <p className="text-xs text-slate-400 mt-1">When readers share their journal entries publicly, they will appear here.</p>
              </div>
            ) : (
              publicJournals.map((journal) => (
                <div key={journal.journal_id} className="bg-white rounded-xl border p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <PenLine className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 text-sm">{journal.user_name || 'Reader'}</p>
                      <p className="text-xs text-slate-400">{journal.book_name} - Chapter {journal.chapter} | {new Date(journal.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="space-y-2 pl-10">
                    {journal.favourite_part && (
                      <div>
                        <p className="text-xs font-medium text-emerald-600">Favourite Part</p>
                        <p className="text-sm text-slate-700">{journal.favourite_part}</p>
                      </div>
                    )}
                    {journal.what_learned && (
                      <div>
                        <p className="text-xs font-medium text-emerald-600">What I Learned</p>
                        <p className="text-sm text-slate-700">{journal.what_learned}</p>
                      </div>
                    )}
                    {journal.how_apply && (
                      <div>
                        <p className="text-xs font-medium text-emerald-600">How I'll Apply This</p>
                        <p className="text-sm text-slate-700">{journal.how_apply}</p>
                      </div>
                    )}
                    {/* Custom Question Answers */}
                    {journal.custom_answers && Object.keys(journal.custom_answers).length > 0 && (
                      Object.entries(journal.custom_answers).map(([qId, answer]) => {
                        const question = customQuestions.find(q => q.question_id === qId);
                        return answer ? (
                          <div key={qId}>
                            <p className="text-xs font-medium text-purple-600">{question?.question_text || qId}</p>
                            <p className="text-sm text-slate-700">{answer}</p>
                          </div>
                        ) : null;
                      })
                    )}
                    {journal.journal_text && (
                      <div className="pt-2 border-t border-slate-100">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">{journal.journal_text}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Discussion Tab */}
        {activeTab === 'discussion' && (
          <div className="space-y-6">
            {/* New Comment */}
            <div className="bg-white rounded-2xl border p-4">
              <h3 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-emerald-500" />
                Join the Discussion
              </h3>
              
              <div className="flex gap-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={isAuthenticated ? "Share your thoughts with other readers..." : "Sign in to join the discussion"}
                  disabled={!isAuthenticated}
                  rows={2}
                  className="flex-1"
                />
                <Button
                  onClick={submitComment}
                  disabled={!isAuthenticated || submitting || !newComment.trim()}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white self-end"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              
              {!isAuthenticated && (
                <p className="text-xs text-slate-500 mt-2">
                  <Button variant="link" className="p-0 h-auto text-emerald-600" onClick={() => navigate('/login')}>
                    Sign in
                  </Button> to join the discussion
                </p>
              )}
            </div>

            {/* Comments List */}
            <div className="space-y-3">
              {discussions.length === 0 ? (
                <div className="bg-white rounded-xl border p-6 text-center">
                  <MessageCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No comments yet. Be the first to share!</p>
                </div>
              ) : (
                discussions.map((comment) => (
                  <div key={comment.comment_id} className="bg-white rounded-xl border p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <Users className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{comment.user_name}</p>
                          <p className="text-xs text-slate-400">{new Date(comment.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      
                      {user && comment.user_id === user.user_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-50"
                          onClick={() => deleteComment(comment.comment_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <p className="text-slate-700 ml-10">{comment.content}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReadathonDetail;
